import * as THREE from 'three';
import type { OutfitName } from './outfits';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';

/**
 * A skinned, animated character loaded from a glTF (Quaternius "Ultimate
 * Modular" rigs, CC0). Locomotion clips are cross-faded by speed; a few
 * procedural overrides (head turn, raised phone arm) are layered on top of the
 * animation each frame.
 */

export type CharacterOptions = {
  url: string;
  /** Target standing height in metres. */
  height: number;
  /** Material name → colour, to restyle the flat-shaded model. */
  tints: Record<string, THREE.ColorRepresentation>;
  /**
   * Cover bare skin with clothing: in any mesh that also uses material
   * `ifMeshHas`, the "Skin" material becomes `color` (sleeves, trousers...).
   */
  coverSkin?: { ifMeshHas: string; color: THREE.ColorRepresentation }[];
};

type LocoState = 'Idle' | 'Walk' | 'Run';

/** What the heroine / stranger need from an animated body (glTF or VRM). */
export interface AnimatedModel {
  readonly root: THREE.Group;
  locomotion(speed: number, walkSpeed: number, runSpeed: number, runFrom: number): void;
  playOnce(name: string, fade?: number): void;
  resetPose(): void;
  update(dt: number): void;
  turnHead(yaw: number, pitch?: number): void;
  raiseRightHand(blend: number, low?: number): void;
  handWorld(out: THREE.Vector3): THREE.Vector3;
  headWorld(out: THREE.Vector3): THREE.Vector3;
  /** Centre of the right palm (where a held phone sits). */
  palmWorld?(out: THREE.Vector3): THREE.Vector3;
  /** Unit direction of the right forearm, elbow to wrist. */
  forearmDir?(out: THREE.Vector3): THREE.Vector3;
  setFear?(t: number): void;
  /** 0..1 left hand raised in front of her chest (holding an umbrella). */
  holdLeft?(blend: number): void;
  /** Centre of the left palm. */
  leftPalmWorld?(out: THREE.Vector3): THREE.Vector3;
  /** Change clothes (VRM heroine only). */
  setOutfit?(name: OutfitName): void;
  dispose(): void;
}

const _aq = new THREE.Quaternion();
const _a1 = new THREE.Vector3();
const _a2 = new THREE.Vector3();

/**
 * Rotate a bone so the direction bone -> child points at the world-space target,
 * blended with its current pose. Works whatever the bone's local axes are.
 */
export function aimBone(bone: THREE.Object3D, child: THREE.Object3D, target: THREE.Vector3, blend: number): void {
  bone.updateWorldMatrix(true, false);
  const origin = bone.getWorldPosition(_a1);
  const cur = child.getWorldPosition(_a2).sub(origin).normalize();
  const want = target.clone().sub(origin).normalize();
  _aq.setFromUnitVectors(cur, want);
  const boneWorld = bone.getWorldQuaternion(new THREE.Quaternion());
  const parentWorld = bone.parent ? bone.parent.getWorldQuaternion(new THREE.Quaternion()) : new THREE.Quaternion();
  const aimed = parentWorld.invert().multiply(_aq).multiply(boneWorld);
  bone.quaternion.slerp(aimed, blend);
  bone.updateMatrixWorld(true);
}

const loader = new GLTFLoader();
const _q = new THREE.Quaternion();
const _q2 = new THREE.Quaternion();
const _v1 = new THREE.Vector3();
const _v3 = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);
/** Face mask fit: ellipsoid radii as a share of the head's skin box, and the band it covers (radians from the crown; half-width round from the front). */
const MASK_FIT = { x: 1.05, y: 1.0, z: 1.1 };
const MASK_SHAPE = { top: 1.74, bottom: 2.8, wrap: 1.5 };

export class CharacterModel implements AnimatedModel {
  readonly root = new THREE.Group();
  private readonly model: THREE.Object3D;
  private readonly mixer: THREE.AnimationMixer;
  private readonly actions = new Map<string, THREE.AnimationAction>();
  private loco: LocoState | null = null;
  private oneShot: THREE.AnimationAction | null = null;
  private readonly bones: Record<string, THREE.Bone | undefined> = {};
  private readonly materials: THREE.Material[] = [];

  private constructor(gltf: { scene: THREE.Group; animations: THREE.AnimationClip[] }, opts: CharacterOptions) {
    this.model = gltf.scene;
    // glTF characters face +Z; the game faces -Z.
    this.model.rotation.y = Math.PI;
    const box = new THREE.Box3().setFromObject(this.model);
    const h = box.max.y - box.min.y;
    this.model.scale.setScalar(opts.height / h);
    this.root.add(this.model);

    this.model.traverse((o) => {
      const mesh = o as THREE.SkinnedMesh;
      if (mesh.isMesh) {
        mesh.castShadow = true;
        mesh.receiveShadow = false;
        mesh.frustumCulled = false; // skinned bounds don't follow the animation
        const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        // A multi-material glTF mesh loads as sibling meshes, one per material.
        const names = new Set<string>();
        const parent = mesh.parent;
        const primitiveGroup = !!parent && parent.children.every((c) => (c as THREE.Mesh).isMesh);
        for (const sibling of primitiveGroup && parent ? parent.children : [mesh]) {
          const sm = (sibling as THREE.Mesh).material;
          if (!sm) continue;
          for (const m of Array.isArray(sm) ? sm : [sm]) names.add(m.name);
        }
        const cover = opts.coverSkin?.find((c) => names.has(c.ifMeshHas));
        const restyled = mats.map((m) => {
          const std = (m as THREE.MeshStandardMaterial).clone();
          const tint = m.name === 'Skin' && cover ? cover.color : opts.tints[m.name];
          if (tint !== undefined) std.color.set(tint);
          std.roughness = 0.85;
          std.metalness = 0;
          this.materials.push(std);
          return std;
        });
        mesh.material = Array.isArray(mesh.material) ? restyled : restyled[0];
      }
      if ((o as THREE.Bone).isBone) this.bones[o.name] = o as THREE.Bone;
    });

    this.mixer = new THREE.AnimationMixer(this.model);
    for (const clip of gltf.animations) {
      const action = this.mixer.clipAction(clip);
      this.actions.set(clip.name, action);
    }
  }

  static async load(opts: CharacterOptions): Promise<CharacterModel> {
    const gltf = await loader.loadAsync(opts.url);
    return new CharacterModel(gltf, opts);
  }

  bone(name: string): THREE.Bone | undefined {
    return this.bones[name];
  }

  /** Cross-fade Idle / Walk / Run to match ground speed (m/s). */
  locomotion(speed: number, walkSpeed: number, runSpeed: number, runFrom: number): void {
    if (this.oneShot) return;
    const state: LocoState = speed < 0.12 ? 'Idle' : speed < runFrom ? 'Walk' : 'Run';
    if (state !== this.loco) {
      const next = this.actions.get(state);
      const prev = this.loco ? this.actions.get(this.loco) : undefined;
      if (next) {
        next.reset().setEffectiveWeight(1).play();
        if (prev) prev.crossFadeTo(next, 0.25, false);
      }
      this.loco = state;
    }
    const action = this.actions.get(state);
    if (!action) return;
    if (state === 'Walk') action.timeScale = THREE.MathUtils.clamp(speed / walkSpeed, 0.35, 1.6);
    else if (state === 'Run') action.timeScale = THREE.MathUtils.clamp(speed / runSpeed, 0.7, 1.4);
    else action.timeScale = 1;
  }

  /** Play a clip once and hold its last frame (e.g. the stranger reaching out). */
  playOnce(name: string, fade = 0.2): void {
    const action = this.actions.get(name);
    if (!action) return;
    action.reset();
    action.setLoop(THREE.LoopOnce, 1);
    action.clampWhenFinished = true;
    action.timeScale = 1;
    action.play();
    if (this.loco) this.actions.get(this.loco)?.crossFadeTo(action, fade, false);
    this.oneShot = action;
  }

  /** Back to normal locomotion (after a reset). */
  resetPose(): void {
    this.mixer.stopAllAction();
    this.oneShot = null;
    this.loco = null;
    this.locomotion(0, 1, 1, 99);
  }

  update(dt: number): void {
    this.mixer.update(dt);
    this.model.updateMatrixWorld(true);
  }

  /** Turn the head (and a little of the neck) by `yaw` radians around world up. */
  turnHead(yaw: number, pitch = 0): void {
    const neck = this.bones.Neck;
    const head = this.bones.Head;
    if (neck) this.rotateBoneWorld(neck, _up, yaw * 0.35);
    if (head) {
      this.rotateBoneWorld(head, _up, yaw * 0.65);
      if (pitch !== 0) {
        _v1.set(1, 0, 0).applyQuaternion(this.root.getWorldQuaternion(_q2));
        this.rotateBoneWorld(head, _v1, pitch);
      }
    }
  }

  /**
   * Raise the right arm so the hand sits in front of the chest, as if holding
   * a phone. `blend` 0..1 mixes from the animated pose. `low` aims the hand
   * further out and lower (holding a flashlight).
   */
  raiseRightHand(blend: number, low = 0): void {
    if (blend <= 0.001) return;
    const upper = this.bones['UpperArm.R'];
    const lower = this.bones['LowerArm.R'];
    const wrist = this.bones['Wrist.R'];
    if (!upper || !lower || !wrist) return;
    // Targets in root space (character faces -Z, right is +X), metres.
    const elbow = _v3.set(0.2, 1.05 - low * 0.08, -0.12 - low * 0.08);
    aimBone(upper, lower, this.root.localToWorld(elbow.clone()), blend);
    const hand = _v3.set(0.1 + low * 0.08, 1.2 - low * 0.18, -0.36 - low * 0.12);
    aimBone(lower, wrist, this.root.localToWorld(hand.clone()), blend);
  }

  /** World position of the right hand. */
  handWorld(out: THREE.Vector3): THREE.Vector3 {
    const wrist = this.bones['Wrist.R'];
    if (!wrist) return this.root.localToWorld(out.set(0.2, 1, -0.2));
    return wrist.getWorldPosition(out);
  }

  /**
   * A surgical mask over the nose and mouth, riding on the Head bone. It is
   * fitted to this model's face: part of an ellipsoid round the skin that
   * follows the head, from the bridge of the nose to under the chin, with
   * pleats, a slight bulge off the face and loops back to the ears.
   */
  addFaceMask(color: THREE.ColorRepresentation): void {
    const head = this.bones.Head;
    if (!head) return;
    this.resetPose();
    this.root.updateMatrixWorld(true);
    const toRoot = new THREE.Matrix4().copy(this.root.matrixWorld).invert();
    const box = new THREE.Box3();
    const v = new THREE.Vector3();
    this.model.traverse((o) => {
      const mesh = o as THREE.SkinnedMesh;
      if (!mesh.isSkinnedMesh || Array.isArray(mesh.material) || mesh.material.name !== 'Skin') return;
      const bone = mesh.skeleton.bones.indexOf(head);
      if (bone < 0) return;
      mesh.skeleton.update();
      const index = mesh.geometry.attributes.skinIndex;
      const weight = mesh.geometry.attributes.skinWeight;
      for (let i = 0; i < index.count; i++) {
        let w = 0;
        for (let k = 0; k < 4; k++) if (index.getComponent(i, k) === bone) w += weight.getComponent(i, k);
        if (w < 0.5) continue;
        mesh.getVertexPosition(i, v);
        box.expandByPoint(v.applyMatrix4(mesh.matrixWorld).applyMatrix4(toRoot));
      }
    });
    if (box.isEmpty()) return;
    const c = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const rx = size.x * 0.5 * MASK_FIT.x;
    const ry = size.y * 0.5 * MASK_FIT.y;
    const rz = size.z * 0.5 * MASK_FIT.z;
    const positions: number[] = [];
    const indices: number[] = [];
    // A patch of the ellipsoid: phi round from the front, theta down from the crown.
    const patch = (phi0: number, phi1: number, th0: number, th1: number, cols: number, rows: number, bulge: number) => {
      const start = positions.length / 3;
      for (let j = 0; j <= rows; j++) {
        const tv = j / rows;
        const th = th0 + (th1 - th0) * tv;
        for (let i = 0; i <= cols; i++) {
          const phi = phi0 + (phi1 - phi0) * (i / cols);
          // Stands off the face most at the front, with three pleats across it.
          const k = 1 + bulge * Math.cos(phi) ** 2 * Math.sin(tv * Math.PI) + (bulge > 0 ? 0.012 * Math.sin(tv * Math.PI * 6) : 0);
          positions.push(
            c.x + rx * k * Math.sin(th) * Math.sin(phi),
            c.y + ry * Math.cos(th),
            c.z - rz * k * Math.sin(th) * Math.cos(phi),
          );
        }
      }
      for (let j = 0; j < rows; j++) {
        for (let i = 0; i < cols; i++) {
          const a = start + j * (cols + 1) + i;
          const b = a + cols + 1;
          indices.push(a, b, a + 1, a + 1, b, b + 1);
        }
      }
    };
    const { top, bottom, wrap } = MASK_SHAPE;
    patch(-wrap, wrap, top, bottom, 16, 8, 0.12);
    // Ear loops: thin bands on round to the sides of the head.
    const mid = (top + bottom) / 2;
    patch(wrap, wrap + 0.75, mid - 0.025, mid + 0.025, 4, 1, 0);
    patch(-wrap - 0.75, -wrap, mid - 0.025, mid + 0.025, 4, 1, 0);
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setIndex(indices);
    // Root space -> Head bone space, so it turns with his head.
    const headInRoot = new THREE.Matrix4().multiplyMatrices(toRoot, head.matrixWorld);
    geometry.applyMatrix4(headInRoot.invert());
    geometry.computeVertexNormals();
    const material = new THREE.MeshStandardMaterial({ color, roughness: 0.92, metalness: 0, side: THREE.DoubleSide });
    this.materials.push(material);
    const mask = new THREE.Mesh(geometry, material);
    mask.name = 'face-mask';
    mask.castShadow = true;
    head.add(mask);
  }

  headWorld(out: THREE.Vector3): THREE.Vector3 {
    const head = this.bones.Head;
    if (!head) return this.root.localToWorld(out.set(0, 1.5, 0));
    return head.getWorldPosition(out);
  }

  private rotateBoneWorld(bone: THREE.Bone, axis: THREE.Vector3, angle: number): void {
    if (Math.abs(angle) < 1e-4) return;
    bone.updateWorldMatrix(true, false);
    _q.setFromAxisAngle(axis, angle);
    const boneWorld = bone.getWorldQuaternion(new THREE.Quaternion());
    const parentWorld = bone.parent ? bone.parent.getWorldQuaternion(new THREE.Quaternion()) : new THREE.Quaternion();
    bone.quaternion.copy(parentWorld.invert().multiply(_q).multiply(boneWorld));
    bone.updateMatrixWorld(true);
  }

  dispose(): void {
    this.mixer.stopAllAction();
    for (const m of this.materials) m.dispose();
    this.model.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (mesh.isMesh) mesh.geometry.dispose();
    });
  }
}
